import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Compass, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavIndex } from '../components/Layout/navIndex';

// The header's search palette listens for this event (Header.jsx), so "Search Rostery" opens it
// without reaching into the header.
function openSearchPalette() {
  window.dispatchEvent(new Event('rostery:open-search'));
}

// A link that goes nowhere. Two ways to render:
//  - inside the app shell (signed in): a block in the content area, so the sidebar, header and
//    search stay put — with "Go to dashboard" and "Search Rostery";
//  - bare (signed out, or while the route still renders it outside the shell): a centred card
//    with "Sign in", or "Go to dashboard" for someone already signed in.
// It knows it is inside the shell because the shell's nav index is present; `bare` forces the
// stand-alone card.
export default function NotFound({ bare = false }) {
  const { user } = useAuth() || {};
  const { pathname, search } = useLocation();
  const nav = useNavIndex();
  const inShell = !bare && !!nav;
  const attempted = `${pathname}${search}`;

  const body = (
    <>
      <span className="w-11 h-11 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center" aria-hidden="true">
        <Compass size={20} />
      </span>
      <p className="mt-5 text-4xl font-semibold tracking-tight text-slate-900 tabular-nums" aria-hidden="true">404</p>
      <h1 className="mt-2 text-lg font-semibold text-slate-900">Page not found</h1>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
        We couldn't find that page. It may have moved, or the link is out of date.
      </p>
      <p className="mt-4 min-w-0">
        <span className="sr-only">Address you tried: </span>
        <code className="inline-block max-w-full truncate align-bottom rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700" title={attempted}>
          {attempted}
        </code>
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {user
          ? <Link to="/" className="btn-primary">Go to dashboard</Link>
          : <Link to="/login" className="btn-primary">Sign in</Link>}
        {user && inShell && (
          <button type="button" onClick={openSearchPalette} className="btn-secondary">
            <Search size={15} aria-hidden="true" /> Search Rostery
          </button>
        )}
      </div>
    </>
  );

  if (inShell) {
    return (
      <div className="w-full max-w-2xl mx-auto py-4 sm:py-10">
        <div className="card p-6 sm:p-8">{body}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <main className="w-full max-w-md">
        <div className="card p-6 sm:p-8">{body}</div>
        <p className="text-center text-xs text-slate-500 mt-6">Rostery</p>
      </main>
    </div>
  );
}
